import { supabase, authHeader, type DocType } from '../supabase'
import { currentOrgId } from '../composables/useOrgSettings'

const TABLE: Record<DocType, 'invoices' | 'quotations' | 'credit_notes'> = {
  invoice: 'invoices',
  quotation: 'quotations',
  credit_note: 'credit_notes'
}

export const docTable = (t: DocType) => TABLE[t]

export function computeTotals(lines: { quantity: number; unit_price: number }[], taxRate: number) {
  const subtotal = +lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0), 0).toFixed(2)
  const tax_amount = +(subtotal * (Number(taxRate) || 0) / 100).toFixed(2)
  const total = +(subtotal + tax_amount).toFixed(2)
  return { subtotal, tax_amount, total }
}

export async function authedFetch(url: string, init: RequestInit = {}) {
  const hdrs = await authHeader()
  const isForm = init.body instanceof FormData
  return fetch(url, {
    ...init,
    headers: {
      ...(init.headers || {}),
      ...hdrs,
      ...(isForm ? {} : { 'content-type': 'application/json' }),
    },
  })
}

export type OrgMembership = { id: string; name: string; slug: string; legal_name: string; role: string }

export async function fetchMyOrgs(): Promise<{ orgs: OrgMembership[]; active_org_id: string | null }> {
  const res = await authedFetch('/api/me/orgs')
  if (!res.ok) throw new Error(await res.text())
  return res.json()
}

export async function switchActiveOrg(orgId: string): Promise<void> {
  const res = await authedFetch('/api/me/active-org', {
    method: 'POST',
    body: JSON.stringify({ org_id: orgId })
  })
  if (!res.ok) throw new Error(await res.text())
}

export async function issueDoc(type: DocType, id: string) {
  const res = await authedFetch(`/api/billing/issue/${type}/${id}`, { method: 'POST' })
  if (!res.ok) throw new Error(await res.text())
  return res.json()
}

export async function fetchPdf(type: DocType, id: string): Promise<Blob> {
  const res = await authedFetch(`/api/billing/pdf/${type}/${id}`)
  if (!res.ok) throw new Error(await res.text())
  return res.blob()
}

export async function saveLines(type: DocType, docId: string, lines: { description: string; quantity: number; unit_price: number }[]) {
  await supabase.from('document_lines').delete().eq('doc_type', type).eq('doc_id', docId)
  if (!lines.length) return
  const oid = currentOrgId()
  const rows = lines
    .filter(l => l.description || l.quantity || l.unit_price)
    .map((l, i) => ({ doc_type: type, doc_id: docId, line_no: i + 1, description: l.description, quantity: Number(l.quantity) || 0, unit_price: Number(l.unit_price) || 0, org_id: oid }))
  if (rows.length) await supabase.from('document_lines').insert(rows)
}

export async function loadLines(type: DocType, docId: string) {
  const { data } = await supabase.from('document_lines').select('*').eq('doc_type', type).eq('doc_id', docId).order('line_no')
  return data || []
}

export function docNoOf(doc: any): string {
  return doc?.invoice_no || doc?.quotation_no || doc?.credit_note_no || '(draft)'
}

export function fmtMoney(n: number, currency: string) {
  const v = Number(n || 0).toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return `${currency} ${v}`
}

export function fmtShortDate(d: string | null) {
  if (!d) return '—'
  const dt = new Date(d)
  return dt.toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' })
}

export const CURRENCIES = ['SGD', 'USD', 'EUR', 'MYR', 'AUD', 'GBP', 'JPY', 'CNY', 'INR', 'HKD']

export async function logAuditEvent(action: string, opts: { table?: string; row_id?: string; context?: any } = {}) {
  try {
    await authedFetch('/api/audit/event', {
      method: 'POST',
      body: JSON.stringify({ action, table: opts.table, row_id: opts.row_id, context: opts.context })
    })
  } catch { /* don't break UI on audit failure */ }
}

export async function requestEdit(table: 'invoices' | 'quotations' | 'credit_notes', row_id: string, proposed: Record<string, any>, reason: string, proposed_lines?: any[]) {
  const res = await authedFetch('/api/audit/request-edit', {
    method: 'POST',
    body: JSON.stringify({ table, row_id, proposed, reason, proposed_lines })
  })
  if (!res.ok) throw new Error(await res.text())
  return res.json()
}

export async function decideRequest(logId: string, decision: 'approved' | 'rejected') {
  const res = await authedFetch(`/api/audit/decide/${logId}`, {
    method: 'POST',
    body: JSON.stringify({ decision })
  })
  if (!res.ok) throw new Error(await res.text())
  return res.json()
}
